import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from './session';
import { Mark } from '@/components/layout/mark';

/**
 * The auth gate.
 *
 * The access token lives in memory, so a reload starts with nothing. The
 * refresh cookie is what survives, and one `POST /auth/refresh` turns it back
 * into a session. Until that resolves we are neither signed in nor signed out,
 * and showing the sign-in page during that window would flash a login screen at
 * someone who is already signed in on every single reload.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'restoring') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ground">
        <Mark className="h-6 w-6 animate-notch-pulse" />
        <span className="sr-only">Checking your session</span>
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
